import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container, Typography, Button, Box, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
  Chip, MenuItem, Select, FormControl, InputLabel,
  Alert, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

const API = process.env.REACT_APP_API_URL;

export default function Usuarios() {
  const { usuario } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'miembro'
  });
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [descargando, setDescargando] = useState(false);

  const cargarUsuarios = () => {
    axios.get(`${API}/api/usuarios`, { params: { empresa_id: usuario?.empresa_id } })
      .then(r => setUsuarios(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUsuarios([]));
  };

  useEffect(() => {
    cargarUsuarios();
    // Setear fechas por defecto — primer y último día del mes actual
    const hoy = new Date();
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    setFechaInicio(primerDia.toISOString().split('T')[0]);
    setFechaFin(ultimoDia.toISOString().split('T')[0]);
  }, []);

  const crearUsuario = async () => {
    try {
      await axios.post(`${API}/api/usuarios?admin_id=${usuario.id}`, form);
      setForm({ nombre: '', email: '', password: '', rol: 'miembro' });
      setError('');
      setOpen(false);
      cargarUsuarios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const eliminarUsuario = async (id) => {
    if (window.confirm('¿Eliminar este usuario?')) {
      await axios.delete(`${API}/api/usuarios/${id}?admin_id=${usuario.id}`);
      cargarUsuarios();
    }
  };

  const descargarCSV = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Por favor selecciona un rango de fechas');
      return;
    }
    setDescargando(true);
    try {
      const res = await axios.get(
        `${API}/api/tiempos/reporte/usuarios?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`
      );
      const registros = res.data;

      if (registros.length === 0) {
        alert('No hay registros en ese rango de fechas');
        setDescargando(false);
        return;
      }

      // Detalle
      const detalle = [
        ['DETALLE DE TIEMPOS'],
        [`Período: ${fechaInicio} al ${fechaFin}`],
        [],
        ['Usuario', 'Proyecto', 'Tipo', 'Fecha', 'Horas', 'Descripción'],
        ...registros.map(r => [r.usuario, r.proyecto, r.tipo, r.fecha, r.horas, r.descripcion])
      ];

      // Resumen por usuario
      const resumenMap = {};
      registros.forEach(r => {
        if (!resumenMap[r.usuario]) {
          resumenMap[r.usuario] = { total: 0, billable: 0, non_billable: 0, descanso: 0, festivo: 0 };
        }
        resumenMap[r.usuario].total += r.horas;
        resumenMap[r.usuario][r.tipo] += r.horas;
      });

      const resumen = [
        [],
        ['RESUMEN POR USUARIO'],
        ['Usuario', 'Total Horas', 'Billable', 'Non-Billable', 'Descanso', 'Festivo'],
        ...Object.entries(resumenMap).map(([nombre, data]) => [
          nombre,
          data.total,
          data.billable,
          data.non_billable,
          data.descanso,
          data.festivo
        ])
      ];

      const csv = [...detalle, ...resumen]
        .map(row => row.join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_tiempos_${fechaInicio}_${fechaFin}.csv`;
      a.click();
    } catch (err) {
      alert('Error al descargar el reporte');
    }
    setDescargando(false);
  };

  if (usuario?.rol !== 'admin' && usuario?.rol !== 'super_admin') {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">No tienes permisos para ver esta página.</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>

      {/* Sección reporte */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          📊 Reporte de Tiempos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Fecha inicio" type="date"
            value={fechaInicio}
            onChange={e => setFechaInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Fecha fin" type="date"
            value={fechaFin}
            onChange={e => setFechaFin(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={descargarCSV}
            disabled={descargando}
          >
            {descargando ? 'Descargando...' : 'Descargar CSV'}
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ mb: 4 }} />

      {/* Sección usuarios */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Usuarios</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Nuevo Usuario
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Nombre</b></TableCell>
              <TableCell><b>Email</b></TableCell>
              <TableCell><b>Rol</b></TableCell>
              <TableCell><b>Acciones</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map(u => (
              <TableRow key={u.id}>
                <TableCell>{u.nombre}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Chip
                    label={u.rol}
                    color={u.rol === 'admin' ? 'error' : 'primary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {u.id !== usuario.id && (
                    <Button
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => eliminarUsuario(u.id)}
                    >
                      Eliminar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog crear usuario */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Nuevo Usuario</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          <TextField
            fullWidth label="Nombre" value={form.nombre}
            onChange={e => setForm({ ...form, nombre: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth label="Email" type="email" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth label="Contraseña" type="password" value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select
              value={form.rol}
              label="Rol"
              onChange={e => setForm({ ...form, rol: e.target.value })}
            >
              <MenuItem value="miembro">Miembro</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crearUsuario}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}